/* eslint-disable no-useless-escape */

export const validateAddressLine1 = (value: string): string | null => {
  if (!value || value.trim().length === 0) {
    return null;
  }

  // Allow letters (including Unicode), numbers, spaces, and special characters
  const validPattern =
    /^([-''()_,.|`''"º°ª\&\s]*[a-zA-Z\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff0-9\u1E00-\u1EFF]+)+([-''()_,./`''"º°ª\&\s]*[a-zA-Z\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff0-9\u1E00-\u1EFF]*)*$/;
  if (!validPattern.test(value)) {
    return "line1InvalidChars";
  }

  return null;
};

export const validateAddressLine2 = (value: string): string | null => {
  if (!value || value.trim().length === 0) {
    return null;
  }

  // Allow letters (including Unicode), numbers, spaces, and special characters
  const validPattern =
    /^([-''()_,.|`''"º°ª\&\s]*[a-zA-Z\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff0-9\u1E00-\u1EFF]+)+([-''()_,./`''"º°ª\&\s]*[a-zA-Z\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff0-9\u1E00-\u1EFF]*)*$/;
  if (!validPattern.test(value)) {
    return "line2InvalidChars";
  }

  return null;
};

export const validateCity = (value: string): string | null => {
  if (!value || value.trim().length === 0) {
    return null;
  }

  // Allow letters (including Unicode), numbers, spaces, and special characters
  const validPattern =
    /^([-''()_,.|`''\\"\\&]*\s*)*[a-zA-Z\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff\u1E00-\u1EFF0-9]+((\s)*[a-zA-Z\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff\u1E00-\u1EFF0-9-''()_,./`''|\\"\\&]*)*$/;
  if (!validPattern.test(value)) {
    return "cityInvalidFormat";
  }

  return null;
};

export const validatePostalCode = (value: string): string | null => {
  if (!value || value.trim().length === 0) {
    return null;
  }

  // Spain postal code: 5 digits with valid province codes (01-52)
  const validPattern = /^(0[1-9]|[1-4][0-9]|5[0-2])\d{3}$/;
  if (!validPattern.test(value.trim())) {
    return "postalCodeInvalidFormat";
  }

  return null;
};

export const isIdentificationOptional = (age: number): boolean => {
  return age < 18;
};

export const identificationRegex = {
  DNI: /^[0-9]{8}[A-Za-z]$/, // 8 digits + 1 letter (e.g., 12345678X)
  NIE: /^[A-Za-z][0-9]{7}[A-Za-z]$/, // letter + 7 digits + letter (e.g., X1234567Z)
  PASSPORT: /^[A-Za-z0-9]{5,12}$/, // 5-12 alphanumeric characters
};

const regexValidation = (input: string, regex: RegExp): boolean => {
  return !regex.test(input);
};

export const validateIdentificationType = (
  type: string | undefined
): string | null => {
  if (!type || type.trim() === "") {
    return null;
  }

  return null;
};

export const validateIdentificationNumber = (
  type: string | undefined,
  number: string | undefined,
  isOptional: boolean = false
): string | null => {
  const hasType = type && type.trim() !== "";
  const hasNumber = number && number.trim() !== "";

  // If optional and both empty: valid
  if (isOptional && !hasType && !hasNumber) {
    return null;
  }

  // If optional and type selected but number empty: valid
  if (isOptional && hasType && !hasNumber) {
    return null;
  }

  // Special error: If number typed but no type selected
  if (!hasType && hasNumber) {
    return "typeRequiredForNumber";
  }

  // If type selected but number empty: valid (let getValidationStatus show warning)
  if (hasType && !hasNumber) {
    return null;
  }

  // If both empty: valid (let getValidationStatus show warning if required)
  if (!hasType && !hasNumber) {
    return null;
  }

  // If both filled, validate format
  if (hasType && hasNumber) {
    const trimmedNumber = number.trim().toUpperCase();

    // Get regex pattern for the selected type
    const regex = identificationRegex[type as keyof typeof identificationRegex];
    if (!regex) {
      return "";
    }

    // Validate using regex pattern
    if (regexValidation(trimmedNumber, regex)) {
      return "identificationNumberInvalidFormat";
    }
  }

  return null;
};

export const getValidationStatus = (
  value: string | undefined,
  errorMessage: string | null,
  isTouched: boolean,
  isRequired: boolean,
  isPreFilled: boolean = false,
  isFocusedBySequence: boolean = false,
  showErrors: boolean = false
): "success" | "error" | "warning" | null => {
  // If field is pre-filled and has value, show validation immediately
  if (isPreFilled && value && value.trim() !== "") {
    if (errorMessage) {
      return "error";
    }
    return "success";
  }

  // If field has a value (user is typing), show validation immediately (onChange behavior)
  if (value && value.trim() !== "") {
    if (errorMessage) {
      return "error";
    }
    return "success";
  }

  // If showErrors is true, treat all fields as touched (triggered when Continue is clicked)
  if (showErrors) {
    if (errorMessage) {
      return "error";
    }
    if (isRequired && (!value || value.trim() === "")) {
      return "warning";
    }
    return null;
  }

  // For empty fields, only show warning after touched OR focused by sequence (onBlur behavior)
  if (!isTouched && !isFocusedBySequence) {
    return null;
  }

  if (errorMessage) {
    return "error";
  }

  if (isRequired && (!value || value.trim() === "")) {
    return "warning";
  }

  return null;
};

export interface FormCompletionCheck {
  organiser: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    postalCode: string;
    identificationType: string;
    identificationNumber: string;
  };
  travellers: Array<{
    age: number;
    identificationType?: string;
    identificationNumber?: string;
  }>;
}

export const isFormComplete = (formData: FormCompletionCheck): boolean => {
  // Check organiser address fields - must be filled AND pass validation
  if (!formData.organiser.addressLine1?.trim()) return false;
  if (validateAddressLine1(formData.organiser.addressLine1)) return false;
  if (validateAddressLine2(formData.organiser.addressLine2 || "")) return false;
  if (!formData.organiser.city?.trim()) return false;
  if (validateCity(formData.organiser.city)) return false;
  if (!formData.organiser.postalCode?.trim()) return false;
  if (validatePostalCode(formData.organiser.postalCode)) return false;

  // Check organiser identification (always required)
  if (!formData.organiser.identificationType?.trim()) return false;
  if (validateIdentificationType(formData.organiser.identificationType))
    return false;
  if (!formData.organiser.identificationNumber?.trim()) return false;
  if (
    validateIdentificationNumber(
      formData.organiser.identificationType,
      formData.organiser.identificationNumber,
      false // Never optional for organiser
    )
  ) {
    return false;
  }

  // Check traveller identifications
  for (const traveller of formData.travellers) {
    const optional = isIdentificationOptional(traveller.age);

    // For required travellers (age >= 18), both type and number must be filled
    if (!optional) {
      if (!traveller.identificationType?.trim()) return false;
      if (!traveller.identificationNumber?.trim()) return false;

      const typeError = validateIdentificationType(
        traveller.identificationType
      );
      if (typeError) return false;

      const numberError = validateIdentificationNumber(
        traveller.identificationType,
        traveller.identificationNumber,
        false
      );
      if (numberError) return false;
    }

    if (optional) {
      const hasNumber = traveller.identificationNumber?.trim();

      // If number is provided, type must also be provided
      if (hasNumber) {
        if (!traveller.identificationType?.trim()) return false;

        const numberError = validateIdentificationNumber(
          traveller.identificationType,
          traveller.identificationNumber,
          true
        );
        if (numberError) return false;
      }
    }
  }

  return true;
};

export interface GetErrorDetailsParams {
  showErrors: boolean;
  organiserValidation: {
    addressLine1: "success" | "error" | "warning" | null;
    addressLine2: "success" | "error" | "warning" | null;
    city: "success" | "error" | "warning" | null;
    postalCode: "success" | "error" | "warning" | null;
    identificationType: "success" | "error" | "warning" | null;
    identificationNumber: "success" | "error" | "warning" | null;
  };
  travellersValidation: Record<
    string,
    {
      identificationType: "success" | "error" | "warning" | null;
      identificationNumber: "success" | "error" | "warning" | null;
    }
  >;
  travellersData: Array<{ encryptedId: string }>;
}

export const getErrorDetails = (
  params: GetErrorDetailsParams
): { count: number; firstError: string | null } => {
  const {
    showErrors,
    organiserValidation,
    travellersValidation,
    travellersData,
  } = params;

  let errorObj = { count: 0, firstError: null as string | null };

  if (!showErrors) {
    return errorObj;
  }

  // Define the order of fields to check
  const organiserFields = [
    { key: "addressLine1", status: organiserValidation.addressLine1 },
    { key: "addressLine2", status: organiserValidation.addressLine2 },
    { key: "city", status: organiserValidation.city },
    { key: "postalCode", status: organiserValidation.postalCode },
    {
      key: "identificationType",
      status: organiserValidation.identificationType,
    },
    {
      key: "identificationNumber",
      status: organiserValidation.identificationNumber,
    },
  ];

  // Check organiser fields
  for (const field of organiserFields) {
    if (field.status === "error" || field.status === "warning") {
      errorObj.count++;
      if (!errorObj.firstError) {
        errorObj.firstError = `organiser-${field.key}`;
      }
    }
  }

  // Check traveller fields
  for (const traveller of travellersData) {
    const validation = travellersValidation[traveller.encryptedId];
    if (!validation) continue;

    const travellerFields = [
      {
        key: "identificationType",
        status: validation.identificationType,
      },
      {
        key: "identificationNumber",
        status: validation.identificationNumber,
      },
    ];

    for (const field of travellerFields) {
      if (field.status === "error" || field.status === "warning") {
        errorObj.count++;
        if (!errorObj.firstError) {
          errorObj.firstError = `traveller-${traveller.encryptedId}-${field.key}`;
        }
      }
    }
  }

  return errorObj;
};
